import React from "react";
import CONTACT from "../../assets/images/contact.png";
import { FieldArray, FormikProps } from "formik";
import { useAppSelector } from "../../app/hooks";
import { IInfant, IReviewBookingDetails } from "./reviewbookingModel";
import { INITIALS } from "../../_constants/constant";
import moment from "moment";
import { getFieldError, getSelectedItems } from "../../_helpers/miscUtils";
import { handleUppercaseChange } from "../../_helpers/formatUtils";
import { FormField } from "../../components/formfield/FormField";

interface InfantFormProps {
  formik: FormikProps<IReviewBookingDetails>;
  InfantRequireDetails: any;
  INFANT: number;
  fieldRefs: React.MutableRefObject<any>;
  handleCheckboxChange: (
    type: "adults" | "children" | "infants",
    flightDetailsKey: "ADULT" | "CHILD" | "INFANT",
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
}

const InfantForm: React.FC<InfantFormProps> = ({
  formik,
  handleCheckboxChange,
  InfantRequireDetails,
  INFANT,
  fieldRefs,
}) => {
  const reviewBookingDetails = useAppSelector(
    (state) => state.reviewbooking.reviewBookingDetails
  );
  const CountryList = useAppSelector((state) => state.country.CountryList);

  const renderTextInput = (
    index: number,
    fieldName: keyof IInfant,
    label: string,
    required: boolean,
    uppercase = false
  ) => {
    const fieldPath = `infants[${index}].${fieldName}`;
    const error = getFieldError<IInfant>(
      formik.errors.infants,
      index,
      fieldName
    );
    const touched = formik.touched.infants?.[index]?.[fieldName] as
      | boolean
      | undefined;

    return (
      <FormField
        label={label}
        required={required}
        error={error}
        touched={touched}
      >
        <input
          type="text"
          className={touched && error ? "error-border" : ""}
          name={fieldPath}
          // ref={(el) => (fieldRefs.current[fieldPath] = el)}
          onChange={
            uppercase
              ? (e) => handleUppercaseChange(formik, e)
              : formik.handleChange
          }
          value={formik.values.infants[index][fieldName] as string}
        />
      </FormField>
    );
  };

  const renderDateInput = (
    index: number,
    fieldName: keyof IInfant,
    label: string,
    required: boolean,
    minDate?: string,
    maxDate?: string
  ) => {
    const fieldPath = `infants[${index}].${fieldName}`;
    const error = getFieldError<IInfant>(
      formik.errors.infants,
      index,
      fieldName
    );
    const touched = formik.touched.infants?.[index]?.[fieldName] as
      | boolean
      | undefined;

    return (
      <FormField
        label={label}
        required={required}
        error={error}
        touched={touched}
      >
        <input
          type="date"
          name={fieldPath}
          // ref={(el) => (fieldRefs.current[fieldPath] = el)}
          onChange={formik.handleChange}
          min={minDate}
          max={maxDate}
          value={formik.values.infants[index][fieldName] as string}
          className={touched && error ? "error-border" : ""}
        />
      </FormField>
    );
  };

  const renderSelectInput = (
    index: number,
    fieldName: keyof IInfant,
    label: string,
    required: boolean,
    options: Array<{ value: string; label: string }>,
    placeholder: string
  ) => {
    const fieldPath = `infants[${index}].${fieldName}`;
    const error = getFieldError<IInfant>(
      formik.errors.infants,
      index,
      fieldName
    );
    const touched = formik.touched.infants?.[index]?.[fieldName] as
      | boolean
      | undefined;

    return (
      <FormField
        label={label}
        required={required}
        error={error}
        touched={touched}
      >
        <select
          name={fieldPath}
          // ref={(el) => (fieldRefs.current[fieldPath] = el)}
          value={formik.values.infants[index][fieldName] as string}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          className={touched && error ? "error-border py-0" : "py-0"}
        >
          <option value="" hidden>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </FormField>
    );
  };

  const renderPersonalInfoSection = (index: number) => (
    <div className="field-set align-end">
      {renderSelectInput(
        index,
        "initial",
        "Initial",
        true,
        INITIALS.map((e) => ({ value: e, label: e })),
        "Select Initial"
      )}
      {renderTextInput(index, "first_name", "First Name", true, true)}
      {renderTextInput(index, "last_name", "Last Name", true, true)}
    </div>
  );

  const renderPassportInfoSection = (index: number) => (
    <div className="field-set">
      {renderDateInput(
        index,
        "date_of_birth",
        "Date of Birth",
        InfantRequireDetails?.DOB,
        moment().subtract(2, "years").format("YYYY-MM-DD"),
        moment().format("YYYY-MM-DD")
      )}
      {InfantRequireDetails?.Passport_Number &&
        renderTextInput(index, "passport_no", "Passport No", true, true)}
      {!InfantRequireDetails?.Passport_Number && <div className="fields"></div>}
      {InfantRequireDetails?.Passport_Issuing_Country &&
        renderSelectInput(
          index,
          "issue_country_code",
          "Issue Country",
          true,
          CountryList?.map((e) => ({
            value: e.country_code,
            label: e.country_name,
          })),
          "Select Country"
        )}{" "}
      {!InfantRequireDetails?.Passport_Issuing_Country && (
        <div className="fields"></div>
      )}
    </div>
  );

  const renderAdditionalPassportInfoSection = (index: number) => (
    <div className="field-set mb-3">
      {InfantRequireDetails?.Passport_Expiry &&
        renderDateInput(
          index,
          "expire_date",
          "Passport Expiry Date",
          true,
          moment().add(6, "months").format("YYYY-MM-DD"),
          ""
        )}
      {InfantRequireDetails?.Nationality &&
        renderSelectInput(
          index,
          "nationality",
          "Nationality",
          true,
          CountryList?.map((e) => ({
            value: e.country_code,
            label: e.country_name,
          })),
          "Select Country"
        )}
      {InfantRequireDetails?.PanCard_No &&
        renderTextInput(index, "pancard_number", "Pancard No", true, true)}
      {!InfantRequireDetails?.PanCard_No && <div className="fields"></div>}
    </div>
  );

  return (
    <div className="adult-form">
      <div className="traveller-details-title">
        <p className="m-0">
          <img src={CONTACT} alt="contact" /> INFANT (15 Days - 2 Yrs)
          <span className="m-0 tarveller-error-message">
            {formik.errors?.infantsLimit}
          </span>
        </p>
        <strong>
          {getSelectedItems(formik.values?.infants)?.length}/{INFANT} added
        </strong>
      </div>
      <span className="note">
        <strong>Important:</strong> Enter name as mentioned on your passport or
        Government approved IDs.
      </span>
      <FieldArray name="infants">
        {({ push, remove }) => (
          <>
            {formik.values.infants.map((infant, index) => (
              <div className="child-form" key={index}>
                <div>
                  <input
                    className="form-check"
                    type="checkbox"
                    name={`infants[${index}].isSelected`}
                    checked={formik.values.infants[index].isSelected}
                    onChange={(e) =>
                      handleCheckboxChange("infants", "INFANT", index, e)
                    }
                  />
                  <h5>
                    {reviewBookingDetails?.infants[index]?.first_name
                      ? `${formik.values.infants[index]?.first_name} ${formik.values.infants[index]?.last_name}`
                      : `Infant ${
                          index -
                          (getSelectedItems(
                            reviewBookingDetails?.infants,
                            "first_name"
                          )?.length -
                            1)
                        }`}
                  </h5>
                </div>
                {formik.values.infants[index].isSelected && (
                  <div className="form">
                    {renderPersonalInfoSection(index)}
                    {renderPassportInfoSection(index)}
                    {(InfantRequireDetails?.Passport_Number ||
                      InfantRequireDetails?.Passport_Expiry ||
                      InfantRequireDetails?.PanCard_No ||
                      InfantRequireDetails?.Nationality) &&
                      renderAdditionalPassportInfoSection(index)}
                  </div>
                )}
              </div>
            ))}
            <a
              className="add-more"
              href="javascript:void(0)"
              onClick={() => {
                if (
                  INFANT > getSelectedItems(formik?.values?.infants)?.length
                ) {
                  push({
                    isSelected: true,
                    first_name: "",
                    last_name: "",
                    initial: "",
                    date_of_birth: "",
                    nationality: "",
                    passport_no: "",
                    issue_country_code: "",
                    expire_date: "",
                  });
                } else {
                  formik.setFieldError(
                    "infantsLimit",
                    `You have already selected ${INFANT} INFANT. Remove before adding a new one.`
                  );
                }
              }}
            >
              + Add New Infant
            </a>
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default InfantForm;
